import { FileInfo } from "@/FileSystem/FileInfo";
import { _convertTreeNodeList } from "@/FileSystem/LoadSaveMolModels/ConvertMolModels/_ConvertTreeNodeList";
import { parseAndLoadMoleculeFile } from "@/FileSystem/LoadSaveMolModels/ParseMolModels/ParseMoleculeFiles";
import { getFormatInfoGivenType } from "@/FileSystem/LoadSaveMolModels/Types/MolFormats";
import { getSmilesFromTreeNode, setSmilesOnTreeNode, isSmilesFormat, extractSmilesFromContents, } from "@/FileSystem/LoadSaveMolModels/SmilesCache";
import { messagesApi } from "@/Api/Messages";
import { TreeNodeListCopies } from "./_Copy";
import { TreeNodeListFilters } from "./_Filters";
import { TreeNodeListNodeActions } from "./_NodeActions";
import { randomID } from "@/Core/Utils/MiscUtils";
import { pushToStoreListBulk, getMoleculesFromStore, setStoreVar } from "@/Store/StoreExternalAccess";
import { isVisualizationDeferred } from "@/Core/Utils/CoalescedTask";
import { visualizationApi } from "@/Api/Visualization";
import { updateStylesInViewer } from "@/Core/Styling/StyleManager";
import { store } from "@/Store";
export class TreeNodeList {
    _nodes = [];
    _filters;
    _nodeActions;
    _copy;
    triggerId = ""; // Purpose of this is just to trigger reactivity if needed
    // This is to keep track of titles. It takes a surprisingly long time to
    // generate this set on the fly.
    // private _titles: Set<string> = new Set<string>();
    /**
     * Constructor.
     *
     * @param  {TreeNode[]} [nodes] The nodes to add to the list.
     */
    constructor(nodes) {
        if (nodes) {
            this._nodes = nodes;
            // this._updateTitles();
        }
        this._filters = new TreeNodeListFilters(this);
        this._nodeActions = new TreeNodeListNodeActions(this);
        this._copy = new TreeNodeListCopies(this);
        // For chaining
        return this;
    }
    /**
     * Triggers reactivity. This is useful on rare occasions you need to trigger
     * reactivity explicitly. Tested on the main, root node. May work on others.
     */
    triggerReactivity() {
        this.triggerId = randomID();
        this.nodes = this.nodes.map((n) => n);
        for (const node of this.nodes) {
            node.triggerReactivity();
            node.nodes?.triggerReactivity();
        }
    }
    // private _updateTitles(): void {
    //     this._titles = new Set<string>();
    //     this._nodes.forEach((node) => {
    //         this._titles.add(node.title);
    //     });
    // }
    /**
     * Gets the filters.
     *
     * @returns {TreeNodeListFilters}  The filters.
     */
    get filters() {
        return this._filters;
    }
    /**
     * Gets the copy actions.
     *
     * @returns {TreeNodeListCopies}  The copy actions.
     */
    get copy() {
        return this._copy;
    }
    /**
     * Gets the nodes as an array.
     *
     * @returns {TreeNode[]}  The nodes.
     */
    get nodes() {
        return this._nodes;
    }
    /**
     * Sets the nodes.
     *
     * @param  {TreeNode[]} nodes  The nodes.
     */
    set nodes(nodes) {
        this._nodes = nodes;
        // this._updateTitles();
    }
    /**
     * An easy way to look up nodes in the descendency. Can be a list of
     * criteria (one criteria per level) or a single criteria.
     *
     * @param  {EasyCriterion[]|EasyCriterion} searchCriteria  The criteria.
     * @returns {TreeNodeList}  The nodes that match the criteria.
     */
    lookup(searchCriteria) {
        return this._filters.lookup(searchCriteria);
    }
    /**
     * Add a node to the list.
     *
     * @param  {TreeNode} node  The node to add.
     */
    push(node) {
        // let newTitle = node.title;
        // if (this._titles.size > 0) {
        //     // Does node.title already exist in this list? If so, rename it.
        //     let idx = 0;
        //     while (this._titles.has(newTitle)) {
        //         newTitle = node.title + " (" + ++idx + ")";
        //     }
        //     this._titles.add(newTitle);
        // }
        // node.title = newTitle;
        // // Throw warning if title already exists.
        // const titles = this._nodes.map((n) => n.title);
        // console.log("titles", titles);
        // // If the title already exists, throw warning.
        // if (titles.includes(node.title)) {
        //     const msg =
        //         "Warning: Node with title " +
        //         node.title +
        //         " already exists. When adding nodes, try to make them unique to avoid problems in the data panel!";
        //     // if (GlobalVars.isLocalHost) {
        //     //     alert(msg);
        //     // }
        //     console.warn(msg);
        // }
        this._nodes.push(node);
    }
    /**
     * Gets the number of nodes in this list.
     *
     * @returns {number}  The number of nodes.
     */
    get length() {
        return this._nodes.length;
    }
    /**
     * Gets the node at the specified index.
     *
     * @param  {number} index  The index.
     * @returns {TreeNode}  The node.
     */
    get(index) {
        return this._nodes[index];
    }
    /**
     * Sets the node at the specified index.
     *
     * @param  {number}   index  The index.
     * @param  {TreeNode} node   The node.
     */
    set(index, node) {
        this._nodes[index] = node;
    }
    /**
     * Iterates through the nodes.
     *
     * @param {Function} func  The function to call for each node.
     */
    forEach(func) {
        for (let i = 0; i < this._nodes.length; i++) {
            func(this._nodes[i], i);
        }
    }
    /**
     * Filters the nodes.
     *
     * @param  {Function} func  The function to call for each node. If the
     *                          function returns true, the node is included in
     *                          the result.
     * @returns {TreeNodeList}  The filtered nodes.
     */
    filter(func) {
        return new TreeNodeList(this._nodes.filter(func));
    }
    /**
     * Gets the first node in the list that matches the specified criteria.
     *
     * @param  {Function} func  The function to call for each node. If the
     *                          function returns true, the node is included in
     *                          the result.
     * @returns {TreeNode|undefined}  The first node that matches the criteria,
     *     or undefined if no nodes match the criteria.
     */
    find(func) {
        return this._nodes.find(func);
    }
    /**
     * Maps the nodes to a new array.
     *
     * @param  {Function} func  The function to call for each node. The function
     *                          should return the new value for the node.
     * @returns {any[]}  The mapped values.
     */
    map(func) {
        return this._nodes.map(func);
    }
    /**
     * Removes the first node in the list and returns it.
     *
     * @returns {TreeNode|undefined}  The node that was removed, or undefined if
     *     the list is empty.
     */
    shift() {
        // const firstElement = this._nodes.shift();
        // if (firstElement !== undefined) {
        //     this._titles.delete(firstElement.title);
        // }
        return this._nodes.shift();
    }
    /**
     * Inserts new elements at the start of the list, and returns the new length
     * of the list.
     *
     * @param  {TreeNode} node  The node to insert.
     * @returns {number}  The new length of the list.
     */
    unshift(node) {
        // this._titles.add(node.title);
        return this._nodes.unshift(node);
    }
    /**
     * Removes the last element from the list and returns it. If the list is
     * empty, undefined is returned and the list is not modified.
     *
     * @returns {TreeNode|undefined}  The node that was removed, or undefined if
     *     the list is empty.
     */
    pop() {
        // const lastElement = this._nodes.pop();
        // if (lastElement !== undefined) {
        //     this._titles.delete(lastElement.title);
        // }
        return this._nodes.pop();
    }
    /**
     * Adds all the nodes in the specified list to this list. Acts in place, but
     * also returns this list for chaining.
     *
     * @param  {TreeNodeList} nodeList  The list of nodes to add.
     * @returns {TreeNodeList}  This list.
     */
    extend(nodeList) {
        nodeList.forEach((node) => {
            this.push(node);
        });
        return this;
    }
    /**
     * Calls the specified callback function for all the elements in the list.
     * The return value of the callback function is the accumulated result, and
     * is provided as an argument in the next call to the callback function.
     *
     * @param  {Function} func            The function to call for each node.
     *                                    The function should return the new
     *                                    value for the accumulator.
     * @param  {any}      [initialValue]  The initial value for the accumulator.
     * @returns {any}  The accumulated result.
     */
    reduce(func, initialValue) {
        return this._nodes.reduce(func, initialValue);
    }
    /**
     * Tests whether at least one element in the list passes the test
     * implemented by the provided function.
     *
     * @param  {Function} func  The function to test for each element.
     * @returns {boolean}  `true` if the callback function returns a truthy
     *         value for at least one element in the list. Otherwise, `false`.
     */
    some(func) {
        return this._nodes.some(func);
    }
    /**
     * Sorts the nodes.
     *
     * @param {Function} func  The function to call for each node. The function
     *                         should return a number indicating the sort order.
     * @returns {TreeNodeList}  This list, sorted (for chaining).
     */
    sort(func) {
        this._nodes.sort(func);
        return this;
    }
    /**
     * Gets the nodes as an array.
     *
     * @returns {TreeNode[]}  The nodes.
     */
    toArray() {
        return this._nodes;
    }
    /**
     * Gets the nodes as a serialized array of ITreeNode interfaces. Good for
     * saving to JSON or passing to webworker.
     *
     * @returns {ITreeNode[]}  The serialized nodes.
     */
    serialize() {
        return this._nodes.map((node) => {
            return node.serialize();
        });
    }
    /**
     * Clears the list of nodes.
     */
    clear() {
        this._nodes = [];
        // this._titles.clear();
    }
    /**
     * Loads a molecule into the list.
     *
     * @deprecated Use `parseAndLoadMoleculeFile` from `FileSystem` instead.
     * @param  {ILoadMolParams} params  The parameters for loading the molecule.
     * @returns {Promise<void | TreeNodeList>}  A promise that resolves with the
     *     list of new nodes, or undefined on failure.
     */
    async loadFromFileInfo(params) {
        return parseAndLoadMoleculeFile(params);
    }
    /**
     * Remove a node of given id, as well as any childless nodes that result.
     * In-place operation.
     *
     * @param  {string | TreeNode | null} node  The id of the node to remove, or
     *                                          the node itself.
     */
    removeNode(node) {
        // if (typeof node === "string") {
        //     // If it's a string
        //     this._titles.delete(node);
        // } else if (node) {
        //     // It's a TreeNode
        //     this._titles.delete(node.title);
        // }
        this._nodeActions.remove(node);
    }
    /**
     * Gets all the nodes, whether terminal or not.
     *
     * @returns {TreeNodeList}  The flat array of all nodes.
     */
    get flattened() {
        return this._nodeActions.flattened;
    }
    /**
     * An alias for filters.onlyTerminal. Gets all the terminal nodes. You
     * access this offten enough that it's easier to add this alias.
     *
     * @returns {TreeNodeList}  All the terminal nodes.
     */
    get terminals() {
        return this._filters.onlyTerminal;
    }
    /**
     * Convert this TreeNodeList to a specified molecular format.
     *
     * @param  {string}          targetExt  The extension of the format to
     *                                      convert to.
     * @param  {boolean}         [merge]    Whether to merge the models into a
     *                                      single PDB string. Default is true.
     * @returns {FileInfo[]} The text-formatted (e.g., PDB, MOL2) strings.
     */
    async toFileInfos(targetExt, merge = true) {
        // Determine if in worker
        const inWorker = 
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        typeof WorkerGlobalScope !== "undefined" &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            self instanceof WorkerGlobalScope;
        const formatInfo = getFormatInfoGivenType(targetExt);
        // Start spinner
        let spinnerId;
        if (!inWorker) {
            spinnerId = messagesApi.startWaitSpinner();
        }
        let fileInfos;
        // When converting to SMILES without merging (the per-molecule case),
        // reuse any SMILES already cached on each node and only convert the
        // rest. Merged conversions collapse many molecules into one string, so
        // they bypass the per-node cache.
        if (!merge && isSmilesFormat(targetExt)) {
            fileInfos = await this._toSmilesFileInfosCached(targetExt);
        }
        else {
            fileInfos = await _convertTreeNodeList(this, targetExt, merge);
        }
        // Update the molecule name in the fileInfo contents
        for (let i = 0; i < fileInfos.length; i++) {
            if (formatInfo && formatInfo.updateMolNameInContents) {
                fileInfos[i].contents = formatInfo.updateMolNameInContents(fileInfos[i].contents, this._nodes[i].title);
            }
        }
        if (!inWorker) {
            messagesApi.stopWaitSpinner(spinnerId);
        }
        return fileInfos;
    }
    /**
     * Per-node SMILES conversion with caching. Nodes that already carry a
     * cached SMILES are served from the Data-panel entry; the remainder are
     * converted in one OpenBabel call and their results stamped back onto the
     * nodes. Output is reassembled in input order so callers that align
     * outputs with this._nodes by index keep working.
     *
     * @param {string} targetExt  The SMILES format to convert to.
     * @returns {Promise<FileInfo[]>}  One FileInfo per convertible node.
     */
    async _toSmilesFileInfosCached(targetExt) {
        const results = new Array(this._nodes.length);
        const uncachedNodes = [];
        const uncachedIdxs = [];
        for (let i = 0; i < this._nodes.length; i++) {
            const node = this._nodes[i];
            const cached = getSmilesFromTreeNode(node);
            if (cached !== null) {
                results[i] = new FileInfo({
                    name: `tmpmol.${targetExt}`,
                    contents: cached,
                });
            }
            else {
                uncachedNodes.push(node);
                uncachedIdxs.push(i);
            }
        }
        if (uncachedNodes.length > 0) {
            // _convertTreeNodeList returns one FileInfo per input node when not
            // merging, index-aligned with uncachedNodes.
            const converted = await _convertTreeNodeList(this.newTreeNodeList(uncachedNodes), targetExt, false);
            for (let k = 0; k < uncachedIdxs.length; k++) {
                const fileInfo = converted[k];
                if (fileInfo === undefined) {
                    continue;
                }
                setSmilesOnTreeNode(this._nodes[uncachedIdxs[k]], extractSmilesFromContents(fileInfo.contents));
                results[uncachedIdxs[k]] = fileInfo;
            }
        }
        // Drop any holes (nodes that yielded no output) to keep the array
        // dense, matching _convertTreeNodeList's contract. Safe for SMILES,
        // which defines no updateMolNameInContents that would rely on index
        // alignment with this._nodes.
        return results.filter((f) => f !== undefined);
    }
    /**
     * Prepare and add all nodes in this list to the main molecule tree. If
     * visualization is deferred (via deferVisualization()), the render is
     * skipped here; the deferVisualization wrapper handles it. Otherwise, a
     * render is triggered after adding.
     *
     * @param {string | null} tag                            Plugin tag.
     * @param {boolean}       [reassignIds]                  Reassign node ids.
     *                                                       Default is true.
     * @param {boolean}       [terminalNodeTitleRevisable]   Allow title
     *                                                       revision. Default
     *                                                       is true.
     * @param {boolean}       [resetVisibilityAndSelection]  Reset vis/sel
     *                                                       state. Default is
     *                                                       true.
     */
    async addToMainTree(tag, reassignIds = true, terminalNodeTitleRevisable = true, resetVisibilityAndSelection = true) {
        const preparedNodes = [];
        for (const node of this._nodes) {
            await node.prepareForMainTree(tag, reassignIds, terminalNodeTitleRevisable, resetVisibilityAndSelection);
            preparedNodes.push(node);
        }
        // Single bulk push triggers reactivity only once.
        if (preparedNodes.length > 0) {
            pushToStoreListBulk("molecules", preparedNodes);
        }
        // Set the project title from the first node if not already set.
        if (store.state.projectTitle === "" && preparedNodes.length > 0) {
            const topAncestor = preparedNodes[0].getAncestry(getMoleculesFromStore()).get(0);
            if (topAncestor && topAncestor.title) {
                setStoreVar("projectTitle", topAncestor.title);
            }
        }
        if (!isVisualizationDeferred()) {
            try {
                updateStylesInViewer();
                const viewer = await visualizationApi.viewer;
                await viewer.renderImmediate();
                viewer.zoomOnFocused();
            }
            catch (err) {
                console.warn("Viewer zoom failed after adding to tree:", err);
            }
        }
    }
    /**
     * Creates a new TreeNodeList with the specified nodes.
     *
     * @param  {TreeNode[]} [nodes] The nodes to include in the new list.
     *                              Default is an empty array.
     * @returns {TreeNodeList}  The new TreeNodeList.
     */
    newTreeNodeList(nodes = []) {
        // To avoid circular dependencies
        return new TreeNodeList(nodes);
    }
    /**
     * Merges all the nodes in this list into a single node. This is useful for
     * converting a list of molecules into a single molecule.
     *
     * @param  {string} [topLevelTitle]  The title of the top-level node. If
     *                                   undefined, the title of the first node
     *                                   is used. Default is undefined.
     * @returns {TreeNodeList}  The new list.
     */
    merge(topLevelTitle) {
        // This is where the title is being set to first item. Bad if multiple
        // frames!
        const firstNode = this._nodes[0].shallowCopy();
        if (topLevelTitle) {
            firstNode.title = topLevelTitle;
        }
        for (let i = 1; i < this._nodes.length; i++) {
            const node = this._nodes[i];
            firstNode.mergeInto(node);
        }
        return new TreeNodeList([firstNode]);
    }
}
